import { Container } from '@playcanvas/pcui';
import { Mat4, Vec3, math } from 'playcanvas';

import { Events } from '../events';

const tmpF = new Vec3();
const tmpN = new Vec3();
const tmpU = new Vec3();
const NORTH_LOW_CONFIDENCE_RMS_METERS = 5;
/** Shown under compass when map north is unknown (CSS uppercases). */
const NORTH_NOT_SET_HINT = 'North not set. Align in the editor.';

function mod360(d: number): number {
    return ((d % 360) + 360) % 360;
}

function pad3(n: number): string {
    const x = Math.round(n) % 360;
    return String(x < 0 ? x + 360 : x).padStart(3, '0');
}

function cardinal8(deg: number): string {
    const d = mod360(deg);
    const labels = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return labels[Math.round(d / 45) % 8];
}

/**
 * Aviation-style HSI: rotating heading card, lubber at top, optional GCP north marker when aligned.
 */
class WorkspaceCompassOverlay extends Container {
    private readonly groupCard: SVGGElement;
    private readonly groupNorthMark: SVGGElement;
    private readonly textHdg: SVGTextElement;
    private readonly textBrg: SVGTextElement;
    private readonly hintEl: HTMLDivElement;
    private mapPlotPhase: 'closed' | 'plane' | 'gcps' = 'closed';
    private northPreviewDeg = 0;
    private northKnown = false;
    private northBasis = new Vec3(0, 0, 1);
    private upBasis = new Vec3(0, 1, 0);
    private northResidualMeters: number | null = null;
    private northLowConfidence = false;
    /** Landing / demo embeds: suppress “align GIS” copy when north is already defined on the capture. */
    private readonly hideNorthAlignHint: boolean;
    private lastHeadingDeg = 0;
    /** World XZ azimuth (deg) used for card rotation and fallback; not north-relative. */
    private lastAbsoluteAzimuthDeg = 0;

    constructor(events: Events, args: Record<string, unknown> = {}) {
        args = {
            ...args,
            id: 'workspace-compass-container'
        };
        super(args);

        const w = window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphHideNorthAlignHint?: boolean };
        };
        this.hideNorthAlignHint = w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphHideNorthAlignHint === true;

        this.dom.classList.add('workspace-compass', 'workspace-compass--hsi');

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg') as SVGSVGElement;
        svg.setAttribute('class', 'workspace-compass__svg workspace-compass__svg--hsi');
        svg.setAttribute('viewBox', '0 0 128 132');
        svg.setAttribute('width', '128');
        svg.setAttribute('height', '132');
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', 'Heading indicator');

        const defs = document.createElementNS(svg.namespaceURI, 'defs');

        const lubberGrad = document.createElementNS(defs.namespaceURI, 'linearGradient');
        lubberGrad.setAttribute('id', 'workspace-compass-lubber-grad');
        lubberGrad.setAttribute('x1', '0%');
        lubberGrad.setAttribute('y1', '0%');
        lubberGrad.setAttribute('x2', '100%');
        lubberGrad.setAttribute('y2', '0%');
        const lg0 = document.createElementNS(defs.namespaceURI, 'stop');
        lg0.setAttribute('offset', '0%');
        lg0.setAttribute('stop-color', '#f4f7fb');
        const lg1 = document.createElementNS(defs.namespaceURI, 'stop');
        lg1.setAttribute('offset', '100%');
        lg1.setAttribute('stop-color', '#8a939e');
        lubberGrad.appendChild(lg0);
        lubberGrad.appendChild(lg1);
        defs.appendChild(lubberGrad);

        const northFill = document.createElementNS(defs.namespaceURI, 'linearGradient');
        northFill.setAttribute('id', 'workspace-compass-north-fill');
        northFill.setAttribute('x1', '0%');
        northFill.setAttribute('y1', '0%');
        northFill.setAttribute('x2', '100%');
        northFill.setAttribute('y2', '0%');
        const ng0 = document.createElementNS(defs.namespaceURI, 'stop');
        ng0.setAttribute('offset', '0%');
        ng0.setAttribute('stop-color', '#7af0a0');
        const ng1 = document.createElementNS(defs.namespaceURI, 'stop');
        ng1.setAttribute('offset', '100%');
        ng1.setAttribute('stop-color', '#1e8f4a');
        northFill.appendChild(ng0);
        northFill.appendChild(ng1);
        defs.appendChild(northFill);

        const glow = document.createElementNS(defs.namespaceURI, 'filter');
        glow.setAttribute('id', 'workspace-compass-hsi-glow');
        glow.setAttribute('x', '-30%');
        glow.setAttribute('y', '-30%');
        glow.setAttribute('width', '160%');
        glow.setAttribute('height', '160%');
        const blur = document.createElementNS(defs.namespaceURI, 'feGaussianBlur');
        blur.setAttribute('stdDeviation', '0.8');
        blur.setAttribute('result', 'blur');
        const merge = document.createElementNS(defs.namespaceURI, 'feMerge');
        const m1 = document.createElementNS(defs.namespaceURI, 'feMergeNode');
        m1.setAttribute('in', 'blur');
        const m2 = document.createElementNS(defs.namespaceURI, 'feMergeNode');
        m2.setAttribute('in', 'SourceGraphic');
        merge.appendChild(m1);
        merge.appendChild(m2);
        glow.appendChild(blur);
        glow.appendChild(merge);
        defs.appendChild(glow);

        svg.appendChild(defs);

        const cx = 64;
        const cy = 54;
        const dialG = document.createElementNS(svg.namespaceURI, 'g');
        dialG.setAttribute('transform', `translate(${cx},${cy})`);

        const bezel = document.createElementNS(svg.namespaceURI, 'circle');
        bezel.setAttribute('r', '50');
        bezel.setAttribute('fill', 'rgba(6,10,18,0.52)');
        bezel.setAttribute('stroke', 'rgba(140,175,220,0.35)');
        bezel.setAttribute('stroke-width', '1.2');
        bezel.setAttribute('filter', 'url(#workspace-compass-hsi-glow)');
        dialG.appendChild(bezel);

        this.groupCard = document.createElementNS(svg.namespaceURI, 'g') as SVGGElement;
        this.groupCard.setAttribute('class', 'workspace-compass__card');

        const innerRing = document.createElementNS(svg.namespaceURI, 'circle');
        innerRing.setAttribute('r', '46');
        innerRing.setAttribute('fill', 'rgba(12,18,28,0.35)');
        innerRing.setAttribute('stroke', 'rgba(90,130,180,0.2)');
        innerRing.setAttribute('stroke-width', '0.6');
        this.groupCard.appendChild(innerRing);

        const DEG = math.DEG_TO_RAD;
        const rTickMaj0 = 38;
        const rTickMaj1 = 46;
        const rTickMin0 = 41;
        const rTickMin1 = 46;
        const rLab = 30;

        for (let a = 0; a < 360; a += 10) {
            const t = a * DEG;
            const sx = Math.sin(t);
            const sy = -Math.cos(t);
            const major = a % 30 === 0;
            const line = document.createElementNS(svg.namespaceURI, 'line');
            const r0 = major ? rTickMaj0 : rTickMin0;
            const r1 = major ? rTickMaj1 : rTickMin1;
            line.setAttribute('x1', String(sx * r0));
            line.setAttribute('y1', String(sy * r0));
            line.setAttribute('x2', String(sx * r1));
            line.setAttribute('y2', String(sy * r1));
            line.setAttribute('stroke', major ? 'rgba(200,220,245,0.55)' : 'rgba(160,185,215,0.35)');
            line.setAttribute('stroke-width', major ? '1.15' : '0.65');
            line.setAttribute('stroke-linecap', 'round');
            this.groupCard.appendChild(line);
        }

        const cardinals: { a: number; t: string }[] = [
            { a: 0, t: 'N' },
            { a: 90, t: 'E' },
            { a: 180, t: 'S' },
            { a: 270, t: 'W' }
        ];
        for (const { a, t } of cardinals) {
            const rad = a * DEG;
            const sx = Math.sin(rad);
            const sy = -Math.cos(rad);
            const te = document.createElementNS(svg.namespaceURI, 'text');
            te.setAttribute('x', String(sx * rLab));
            te.setAttribute('y', String(sy * rLab + 3.5));
            te.setAttribute('text-anchor', 'middle');
            te.setAttribute('fill', '#e8f2ff');
            te.setAttribute('font-size', '10');
            te.setAttribute('font-weight', '800');
            te.setAttribute('font-family', 'system-ui,Segoe UI,sans-serif');
            te.textContent = t;
            this.groupCard.appendChild(te);
        }

        for (const a of [30, 60, 120, 150, 210, 240, 300, 330]) {
            const rad = a * DEG;
            const sx = Math.sin(rad);
            const sy = -Math.cos(rad);
            const te = document.createElementNS(svg.namespaceURI, 'text');
            te.setAttribute('x', String(sx * rLab));
            te.setAttribute('y', String(sy * rLab + 2.5));
            te.setAttribute('text-anchor', 'middle');
            te.setAttribute('fill', 'rgba(195,210,230,0.9)');
            te.setAttribute('font-size', '7');
            te.setAttribute('font-weight', '600');
            te.setAttribute('font-family', 'ui-monospace,Consolas,monospace');
            te.textContent = String(a);
            this.groupCard.appendChild(te);
        }

        this.groupNorthMark = document.createElementNS(svg.namespaceURI, 'g') as SVGGElement;
        const nChev = document.createElementNS(svg.namespaceURI, 'path') as SVGPathElement;
        nChev.setAttribute('d', 'M 0 -44 L -3.5 -36 L 3.5 -36 Z');
        nChev.setAttribute('fill', 'url(#workspace-compass-north-fill)');
        nChev.setAttribute('stroke', 'rgba(255,255,255,0.45)');
        nChev.setAttribute('stroke-width', '0.45');
        nChev.setAttribute('stroke-linejoin', 'round');
        this.groupNorthMark.appendChild(nChev);
        this.groupNorthMark.setAttribute('opacity', '0');
        this.groupCard.appendChild(this.groupNorthMark);

        dialG.appendChild(this.groupCard);

        const lubber = document.createElementNS(svg.namespaceURI, 'path') as SVGPathElement;
        lubber.setAttribute('class', 'workspace-compass__lubber');
        lubber.setAttribute('d', 'M 0 -51 L -5.5 -41 L 5.5 -41 Z');
        lubber.setAttribute('fill', 'url(#workspace-compass-lubber-grad)');
        lubber.setAttribute('stroke', 'rgba(255,255,255,0.5)');
        lubber.setAttribute('stroke-width', '0.55');
        lubber.setAttribute('stroke-linejoin', 'round');
        dialG.appendChild(lubber);

        const hub = document.createElementNS(svg.namespaceURI, 'circle');
        hub.setAttribute('r', '3');
        hub.setAttribute('fill', 'rgba(230,238,248,0.95)');
        hub.setAttribute('stroke', 'rgba(60,100,160,0.55)');
        hub.setAttribute('stroke-width', '0.5');
        dialG.appendChild(hub);

        svg.appendChild(dialG);

        this.textHdg = document.createElementNS(svg.namespaceURI, 'text') as SVGTextElement;
        this.textHdg.setAttribute('x', '64');
        this.textHdg.setAttribute('y', '112');
        this.textHdg.setAttribute('text-anchor', 'middle');
        this.textHdg.setAttribute('class', 'workspace-compass__readout-hdg');
        this.textHdg.textContent = 'HDG 000° N';

        this.textBrg = document.createElementNS(svg.namespaceURI, 'text') as SVGTextElement;
        this.textBrg.setAttribute('x', '64');
        this.textBrg.setAttribute('y', '126');
        this.textBrg.setAttribute('text-anchor', 'middle');
        this.textBrg.setAttribute('class', 'workspace-compass__readout-brg');
        this.textBrg.textContent = 'BRG —';

        svg.appendChild(this.textHdg);
        svg.appendChild(this.textBrg);

        this.dom.appendChild(svg);

        this.hintEl = document.createElement('div');
        this.hintEl.className = 'workspace-compass__north-hint';
        this.hintEl.textContent = NORTH_NOT_SET_HINT;
        this.hintEl.setAttribute('aria-live', 'polite');
        this.dom.appendChild(this.hintEl);

        events.on('volugraph.mapPlot.phase', (detail: { phase?: string }) => {
            if (
                detail?.phase === 'closed' ||
                detail?.phase === 'plane' ||
                detail?.phase === 'gcps'
            ) {
                this.mapPlotPhase = detail.phase as typeof this.mapPlotPhase;
            }
        });
        events.on(
            'volugraph.mapPlot.northPreview',
            (detail: {
                deg?: number;
                known?: boolean;
                basisNorth?: number[];
                basisUp?: number[];
                residualMeters?: number;
                lowConfidence?: boolean;
            }) => {
                if (typeof detail?.deg === 'number' && Number.isFinite(detail.deg)) {
                    this.northPreviewDeg = detail.deg;
                }
                if (
                    Array.isArray(detail?.basisNorth) &&
                    detail.basisNorth.length === 3 &&
                    detail.basisNorth.every((x): x is number => typeof x === 'number' && Number.isFinite(x))
                ) {
                    this.northBasis.set(detail.basisNorth[0], detail.basisNorth[1], detail.basisNorth[2]);
                    if (this.northBasis.lengthSq() > 1e-10) {
                        this.northBasis.normalize();
                    }
                }
                if (
                    Array.isArray(detail?.basisUp) &&
                    detail.basisUp.length === 3 &&
                    detail.basisUp.every((x): x is number => typeof x === 'number' && Number.isFinite(x))
                ) {
                    this.upBasis.set(detail.basisUp[0], detail.basisUp[1], detail.basisUp[2]);
                    if (this.upBasis.lengthSq() > 1e-10) {
                        this.upBasis.normalize();
                    }
                }
                this.northKnown = detail?.known === true;
                this.northResidualMeters =
                    typeof detail?.residualMeters === 'number' && Number.isFinite(detail.residualMeters)
                        ? detail.residualMeters
                        : null;
                if (typeof detail?.lowConfidence === 'boolean') {
                    this.northLowConfidence = detail.lowConfidence;
                } else {
                    this.northLowConfidence =
                        this.northResidualMeters !== null && this.northResidualMeters > NORTH_LOW_CONFIDENCE_RMS_METERS;
                }
                this.syncNorthMark();
            }
        );
        this.syncNorthMark();
    }

    private syncNorthMark(): void {
        const compassVisible = this.northKnown;
        this.dom.classList.toggle('workspace-compass--hidden', !compassVisible);
        this.dom.style.setProperty('display', compassVisible ? 'flex' : 'none', 'important');

        if (!compassVisible) {
            this.hintEl.style.display = 'none';
            return;
        }

        // Card rotates by −relative heading (see update); chevron stays at the lubber / top of card.
        this.groupNorthMark.setAttribute('transform', 'rotate(0)');
        this.groupNorthMark.setAttribute('opacity', this.northKnown ? '0.95' : '0');
        if (this.northKnown) {
            this.textBrg.textContent = `BRG ${mod360(this.northPreviewDeg).toFixed(1)}°`;
        } else {
            this.textBrg.textContent = 'BRG —';
        }
        if (!this.northKnown) {
            if (this.hideNorthAlignHint) {
                this.hintEl.style.display = 'none';
            } else {
                this.hintEl.textContent = NORTH_NOT_SET_HINT;
                this.hintEl.style.display = 'block';
            }
            return;
        }
        if (this.northLowConfidence) {
            const rmsTxt = this.northResidualMeters !== null ? this.northResidualMeters.toFixed(1) : `>${NORTH_LOW_CONFIDENCE_RMS_METERS}`;
            this.hintEl.textContent = `ALIGNMENT LOW CONFIDENCE (RMS ${rmsTxt} m)`;
            this.hintEl.style.display = 'block';
            return;
        }
        this.hintEl.style.display = 'none';
    }

    /**
     * Heading is "where screen-up points" on the ground plane. Once GCP north is
     * known, compare that screen-up vector directly against true north in scene
     * space instead of subtracting solver-basis degrees from world-XZ degrees.
     *
     * cameraMatrix: scene camera entity world transform (same as ViewCube / prerender).
     */
    update(cameraMatrix: Mat4): void {
        const d = cameraMatrix.data;
        const fx = -d[8];
        const fy = -d[9];
        const fz = -d[10];
        const ux = d[4];
        const uy = d[5];
        const uz = d[6];

        const fwdXZLen = Math.hypot(fx, fz);
        const fwdNorm = Math.hypot(fx, fy, fz) || 1;
        const pitchFrac = Math.abs(fy) / fwdNorm;

        let absoluteDeg: number;
        let relativeDeg: number;

        if (this.northKnown && this.northBasis.lengthSq() > 1e-10) {
            tmpN.copy(this.northBasis).normalize();
            tmpU.copy(this.upBasis).normalize();
            tmpF.set(ux, uy, uz);
            // Remove any component normal to the horizontal/georeferenced plane.
            tmpF.sub(tmpU.clone().mulScalar(tmpF.dot(tmpU)));
            if (tmpF.lengthSq() < 1e-10) {
                const fallbackRad = this.lastHeadingDeg * math.DEG_TO_RAD;
                relativeDeg = mod360(fallbackRad * math.RAD_TO_DEG);
            } else {
                tmpF.normalize();
                const cx = tmpN.y * tmpF.z - tmpN.z * tmpF.y;
                const cy = tmpN.z * tmpF.x - tmpN.x * tmpF.z;
                const cz = tmpN.x * tmpF.y - tmpN.y * tmpF.x;
                const signedCross = cx * tmpU.x + cy * tmpU.y + cz * tmpU.z;
                relativeDeg = mod360(Math.atan2(signedCross, tmpN.dot(tmpF)) * math.RAD_TO_DEG);
            }
            absoluteDeg = relativeDeg;
        } else {
            if (pitchFrac > 0.52) {
                tmpF.set(ux, 0, uz);
            } else {
                tmpF.set(fx, 0, fz);
            }

            if (tmpF.lengthSq() < 1e-12) {
                const fallbackRad = this.lastAbsoluteAzimuthDeg * math.DEG_TO_RAD;
                tmpF.set(Math.sin(fallbackRad), 0, Math.cos(fallbackRad));
            } else {
                tmpF.normalize();
            }

            // Steep pitch: heading follows camera *up* projected on XZ (top-of-screen on ground plane).
            // Must use atan2(-x, z) to match the GCP north-basis math (see northKnown branch with N=(0,0,1));
            // atan2(+x, z) flips east/west vs orbit controls.
            absoluteDeg =
                pitchFrac > 0.52
                    ? Math.atan2(-tmpF.x, tmpF.z) * math.RAD_TO_DEG
                    : Math.atan2(tmpF.x, tmpF.z) * math.RAD_TO_DEG;
            relativeDeg = mod360(absoluteDeg);
        }

        this.lastAbsoluteAzimuthDeg = absoluteDeg;
        this.lastHeadingDeg = relativeDeg;

        // Rotate card in same direction as derived heading so compass tracks orbit direction visually.
        this.groupCard.setAttribute('transform', `rotate(${relativeDeg})`);

        const h = mod360(relativeDeg);
        this.textHdg.textContent = `HDG ${pad3(h)}° ${cardinal8(h)}`;
    }
}

export { WorkspaceCompassOverlay };
